import { Location, NavigateFunction } from 'react-router-dom';
import { JSONObject } from "models/Api/types";
import { ItemModel, ListingModel } from "models";
import { ItemSearchFiltersFormData, ItemSortFilter } from 'components/Form/ItemSearchFiltersForm/ItemSearchFiltersForm';
import { ListingSearchFiltersFormData, ListingSortFilter } from 'components/Form/ListingSearchFiltersForm/ListingSearchFiltersForm';
import { convertJSONObjectToSnakecase, flattenKeys } from './object';
import { getEligibleQueryParamsFromUrl } from 'utils/url';
import { QuerySearchParameters } from "constants/url";

export function convertSearchFilterDataToQuery(
  searchQuery: ItemSearchFiltersFormData
): JSONObject {
  const queryParams: JSONObject = {};
  Object.entries(searchQuery).forEach((data) => {
    queryParams[data[0]] = data[1];
  });
  const formattedQueryParams: JSONObject = convertJSONObjectToSnakecase(queryParams);
  if ('gt' in queryParams) {
    formattedQueryParams['gt'] = flattenKeys(formattedQueryParams['gt']);
  }

  if ('lt' in queryParams) {
    formattedQueryParams['lt'] = flattenKeys(formattedQueryParams['lt']);
  }
  return formattedQueryParams;
}


export function convertQueryToSearchFilterData(
  searchQuery: JSONObject
): ItemSearchFiltersFormData {
  const searchFilterData: ItemSearchFiltersFormData = {};
  Object.entries(searchQuery).forEach(([key, value]) => {
    if (typeof value === 'string') {
      if (key === 'query') {
        searchFilterData.query = value;
      }
      if (key === 'type') {
        searchFilterData.type = [value];
      }
      if (key === 'materialIds') {
        searchFilterData.materialIds = [value];
      }
      if (key === 'artistId') {
        searchFilterData.artistId = [value];
      }
      if (key === 'sortBy') {
        searchFilterData.sortBy = value as ItemSortFilter;
      }
      if (key === 'sortType') {
        searchFilterData.sortType = value.includes('asc') ? 'asc' : 'desc';
      }
    }

    if (Array.isArray(value)) {
      const stringValues = value.map((entry) => String(entry));
      if (key === 'type' && stringValues.length) {
        searchFilterData.type = stringValues;
      }
      if (key === 'materialIds' && stringValues.length) {
        searchFilterData.materialIds = stringValues;
      }
      if (key === 'artistId' && stringValues.length) {
        searchFilterData.artistId = stringValues;
      }
    }
  });
  return searchFilterData;
}


export function convertListingSearchFilterDataToQuery(
  searchQuery: ListingSearchFiltersFormData,
  showAssociatedItems?: boolean
): JSONObject {
  const queryParams: JSONObject = {};
  Object.entries(searchQuery).forEach((data) => {
    if (Array.isArray(data[1]) && data[1].length === 0) {
      return;
    }
    queryParams[data[0]] = data[1];
  });
  const formattedQueryParams: JSONObject = convertJSONObjectToSnakecase(queryParams);
  if ('gt' in queryParams) {
    formattedQueryParams['gt'] = flattenKeys(formattedQueryParams['gt']);
  }

  if ('lt' in queryParams) {
    formattedQueryParams['lt'] = flattenKeys(formattedQueryParams['lt']);
  }

  if (showAssociatedItems) {
    formattedQueryParams['associate_items'] = true;
  }

  return formattedQueryParams;
}


export function convertListingQueryToSearchFilterData(
  searchQuery: JSONObject
): ListingSearchFiltersFormData {
  const searchFilterData: ListingSearchFiltersFormData = {};
  Object.entries(searchQuery).forEach(([key, value]) => {
    if (typeof value === 'string') {
      if (key === 'query') {
        searchFilterData.query = value;
      }
      if (key === 'type') {
        searchFilterData.type = [value];
      }
      if (key === 'artistId') {
        searchFilterData.artistId = [value];
      }
      if (key === 'isUnique') {
        searchFilterData.isUnique = value === 'true';
      }
      if (key === 'multipleListings') {
        searchFilterData.multipleItems = value === 'true';
      }
      if (key === 'sortBy') {
        searchFilterData.sortBy = value as ListingSortFilter;
      }
      if (key === 'sortType') {
        searchFilterData.sortType = value.includes('asc') ? 'asc' : 'desc';
      }
    }

    if (Array.isArray(value)) {
      const stringValues = value.map((entry) => String(entry));
      if (key === 'type' && stringValues.length) {
        searchFilterData.type = stringValues;
      }
      if (key === 'artistId' && stringValues.length) {
        searchFilterData.artistId = stringValues;
      }
    }

    if (typeof value === 'boolean') {
      if (key === 'isUnique') {
        searchFilterData.isUnique = value;
      }
      if (key === 'multipleListings') {
        searchFilterData.multipleItems = value;
      }
    }
  });
  return searchFilterData;
}


export function filterItemsByQuery(items: ItemModel[], query: string): ItemModel[] {
  const queryWords = query.toLowerCase().split(/\s+/);

  const textQueries = queryWords.filter(word => !/^\d+$/.test(word));
  const numberQueries = queryWords.filter(word => /^\d+$/.test(word)).map(Number);

  return items.filter(item => {
    const itemData = item.name.toLowerCase() + ' ' + item.getArtistName().toLowerCase() + item.material.toLowerCase();

    const isTextMatch = textQueries.length === 0 || textQueries.every(word => itemData.includes(word));

    const isNumberMatch = numberQueries.length === 0 || numberQueries.some(number =>
      (!!item.getReleaseYear() && (item.getReleaseYear() === number || String(item.getReleaseYear()).includes(String(number)))) || (item.name.includes(String(number)))
    );

    return isTextMatch && isNumberMatch;
  });
}

export function filterListingsByQuery(listings: ListingModel[], query: string): ListingModel[] {
  const queryWords = query.toLowerCase().split(/\s+/);

  return listings.filter(listing => {
    const listingData = listing.name.toLowerCase() + ' ' + listing.artist.toLowerCase() + ' ' + listing.source.toLowerCase();
    return queryWords.every(word => listingData.includes(word));
  });
}

type FiltersParamValue = JSONObject | undefined;

function parseFiltersParam(value: unknown): FiltersParamValue {
  if (typeof value !== 'string') {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as JSONObject;
    }
  } catch {
    // swallow parsing errors and treat as undefined
  }

  return undefined;
}

export function extractFiltersFromLocation(
  location: Location,
  filterKeys: QuerySearchParameters[] = [
    QuerySearchParameters.SORT_BY,
    QuerySearchParameters.SORT_TYPE,
    QuerySearchParameters.TYPE,
    QuerySearchParameters.PAGE,
    QuerySearchParameters.LISTING_FILTERS,
    QuerySearchParameters.ITEM_FILTERS,
  ],
) {
  const rawParams = getEligibleQueryParamsFromUrl(location, filterKeys);

  const itemFiltersParam = parseFiltersParam(rawParams[QuerySearchParameters.ITEM_FILTERS]);
  const listingFiltersParam = parseFiltersParam(rawParams[QuerySearchParameters.LISTING_FILTERS]);

  const sanitizedParams: JSONObject = { ...rawParams };
  delete sanitizedParams[QuerySearchParameters.ITEM_FILTERS];
  delete sanitizedParams[QuerySearchParameters.LISTING_FILTERS];

  const itemRelevantKeys = ['sortBy', 'sortType', 'type', 'query', 'artistId', 'materialIds'];
  const listingRelevantKeys = ['sortBy', 'sortType', 'type', 'query', 'artistId', 'isUnique', 'multipleListings'];

  const hasItemRelevantParams = itemRelevantKeys.some((key) => sanitizedParams[key] !== undefined);
  const hasListingRelevantParams = listingRelevantKeys.some((key) => sanitizedParams[key] !== undefined);

  return {
    item: itemFiltersParam
      ? convertQueryToSearchFilterData(itemFiltersParam)
      : hasItemRelevantParams
        ? convertQueryToSearchFilterData(sanitizedParams)
        : undefined,

    listing: listingFiltersParam
      ? convertListingQueryToSearchFilterData(listingFiltersParam)
      : hasListingRelevantParams
        ? convertListingQueryToSearchFilterData(sanitizedParams)
        : convertListingQueryToSearchFilterData({
            sortBy: ListingSortFilter.DATE,
            sortType: 'desc',
            associate_items: true,
          }),
  };
}

interface UpdateSearchFiltersInUrlParams<TFilters> {
  location: Location;
  navigate: NavigateFunction;
  filters?: TFilters;
  filterParamKey: QuerySearchParameters;
  paramMappings?: Array<{
    key: QuerySearchParameters;
    value: string | number | boolean | (string | number | boolean)[] | undefined | null;
  }>;
}

export function updateSearchFiltersInUrl<TFilters>({
  location,
  navigate,
  filters,
  filterParamKey,
  paramMappings = [],
}: UpdateSearchFiltersInUrlParams<TFilters>) {
  const queryParams = new URLSearchParams(location.search);

  if (filters && typeof filters === 'object' && Object.keys(filters as Record<string, unknown>).length > 0) {
    queryParams.set(filterParamKey, JSON.stringify(filters));
  } else {
    queryParams.delete(filterParamKey);
  }

  paramMappings.forEach(({ key, value }) => {
    let resolvedValue: string | null = null;

    if (Array.isArray(value)) {
      resolvedValue = value.length ? String(value[0]) : null;
    } else if (typeof value === 'boolean') {
      resolvedValue = value ? 'true' : null;
    } else if (value !== undefined && value !== null && value !== '') {
      resolvedValue = String(value);
    }

    if (resolvedValue) {
      queryParams.set(key, resolvedValue);
    } else {
      queryParams.delete(key);
    }
  });

  queryParams.delete(QuerySearchParameters.PAGE);
  queryParams.set(QuerySearchParameters.PAGE, '1');

  const newSearch = queryParams.toString();
  if (newSearch !== location.search.replace(/^\?/, '')) {
    navigate(`${location.pathname}?${newSearch}`, { replace: true });
  }
}
